import { useState, useEffect, forwardRef, useImperativeHandle, memo, useMemo } from "react"
import { TextField, Button, Tooltip, Autocomplete, IconButton, Card, CardHeader, CardContent, InputAdornment, Backdrop, CircularProgress } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import { CommonConstant } from "shared/constants/common-constant"
import { rootStore } from "stores/root-store"
import AddIcon from "@mui/icons-material/Add"
import { CustomDataGrid } from "components/common-styles"
import { genId, isItemBOM, linkBomComponents } from "utils/app-helper"
import { GlobalData } from "shared/global-data"
import VirtualizedAutocomplete from "components/common/virtualized-autocomplete"

const SaleOrderProduct = forwardRef(({ erp_order, deliveryCountry, curProducts, isSubmitting }, ref) => {
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const handleInputChange = (e, newValue) => {
    if (newValue.length > 2) {
      setOpen(true)
    } else {
      setOpen(false)
    }
  }

  const countries = useMemo(() => rootStore.getOptionsByFilter("COUNTRY"), [])

  const columns = [
    {
      field: "line_num",
      headerName: "No.",
      width: 100,
      description: "sales order line number",
      renderCell: params => Number(params.value).toFixed(3),
    },
  ]

  columns.push({
    field: "erp_order",
    headerName: "Order Number",
    width: 200,
    renderCell: params => <TextField onChange={event => onPropChanged(params.row["id"], "erp_order", event.target.value)} value={params.value} placeholder={erp_order} />,
  })

  columns.push(
    {
      field: "product",
      headerName: "Product",
      minWidth: 250,
      flex: 1,
      description: "product code / item code / SKU code",
      renderCell: params => (
        <VirtualizedAutocomplete
          id={`product-${params.row.line_num}`}
          //open={open}
          //onInputChange={handleInputChange}
          //onBlur={() => setOpen(false)}
          readOnly={Boolean(params.row.parentProduct)}
          value={params.value}
          isOptionEqualToValue={(option, value) => option.id === value}
          onChange={(event, x) => onPropChanged(params.row["id"], "product", x.id)}
          options={GlobalData.ProductListSelection}
          renderInput={paramsInner => <TextField {...paramsInner} error={Boolean(params.row.validation?.product)} helperText={params.row.validation?.product} />}
        />
      ),
    },
    {
      field: "qty",
      headerName: "Qty",
      width: 130,
      renderCell: params => (
        <TextField
          value={params.value}
          type="number"
          onChange={event => onPropChanged(params.row.id, "qty", event.target.value)}
          error={Boolean(params.row.validation.qty)}
          helperText={params.row.validation.qty || (params.row.exploded ? "Already exploded" : "")}
          inputProps={{ min: 0 }}
          id={`qty-${params.row.line_num}`}
          InputProps={{
            readOnly: Boolean(params.row.parentProduct || params.row.exploded),
          }}
        />
      ),
    },
    {
      field: "country_of_origin",
      headerName: "Country of Origin",
      minWidth: 200,
      flex: 1,
      description: "product's country of manufacture",
      renderCell: params => (
        <Autocomplete
          id={`country_of_origin-${params.row.line_num}`}
          disableClearable
          fullWidth
          readOnly={Boolean(params.row.parentProduct)}
          isOptionEqualToValue={(option, value) => option.id === value}
          value={rootStore.getTextByValue("COUNTRY", params.value)}
          onChange={(event, x) => onPropChanged(params.row.id, "country_of_origin", x.id)}
          options={countries.map(option => ({ label: `${option.value} - ${option.label}`, id: option.value }))}
          renderInput={paramsInner => <TextField {...paramsInner} error={Boolean(params.row.validation.country_of_origin)} helperText={params.row.validation.country_of_origin} />}
        />
      ),
    },
    {
      field: "price",
      headerName: "Price",
      width: 160,
      description: "product's sale price to be declared at customs",
      renderCell: params => (
        <TextField
          id={`price-${params.row.line_num}`}
          value={params.value}
          type="number"
          onChange={event => onPropChanged(params.row.id, "price", event.target.value)}
          error={Boolean(params.row.validation.price)}
          helperText={params.row.validation.price}
          inputProps={{ min: 0 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            readOnly: Boolean(params.row.parentProduct),
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 50,
      // A component line can always be removed on its own; a BOM or normal line only while another one remains
      renderCell: params =>
        (params.row.parentProduct || products.filter(x => !x.parentProduct && x.action !== "DELETE").length > 1) && (
          <Tooltip placement="top" title="Delete">
            <IconButton id={`delete-${params.row.line_num}`} aria-label="actions" onClick={() => onDeleteClicked(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
    },
  )
  const [products, setProducts] = useState([])

  useEffect(() => {
    if (products.length == 0) {
      setProducts([...products, createProduct()])
    }
  }, [products])

  useEffect(() => {
    if (!curProducts) return
    let cancelled = false

    const loadLines = async () => {
      const result = [...curProducts]
      result.forEach(p => {
        p.validation = {}
        p.id = genId()
      })

      // Link each BOM line's components using the BOM's definition (saved orders don't record the link)
      const bomLines = result.filter(isItemBOM)
      if (bomLines.length) {
        setLoading(true)
        const definitions = await Promise.all(bomLines.map(p => rootStore.BillOfMaterialStore.getBomDetails(String(p.product).toUpperCase()).catch(() => [])))
        linkBomComponents(result, bomLines, definitions)
        setLoading(false)
      }

      // A saved BOM that has been exploded (its components are on the order, or Scale left it at qty 0) mustn't be
      // raised again: that would explode it a second time and send duplicate items
      result.forEach(p => {
        p.exploded = isItemBOM(p) && Boolean(p.line_id) && (Number(p.qty) === 0 || result.some(c => c.parentProduct === p.product))
      })

      if (!cancelled) setProducts(result)
    }

    loadLines()
    return () => {
      cancelled = true
    }
  }, [curProducts])

  useEffect(() => {
    if (erp_order && curProducts.length == 0) {
      const result = [...products]
      result.forEach(p => {
        p.erp_order = erp_order
      })
      setProducts(result)
    }
  }, [erp_order])

  useEffect(() => {
    if (products.length == 0) return

    validateData()
  }, [deliveryCountry])

  useImperativeHandle(ref, () => ({
    getProductsIfValid() {
      if (validateData()) return products

      return null
    },
  }))

  const getErrorMessageOfProp = (newProducts, id, prop, val) => {
    const item = newProducts.find(x => x.id === id)
    if (!item) return ""

    const productVal = item.product.toUpperCase()
    const product = (GlobalData.ProductListSelection || []).find(x => x.id.toUpperCase() === productVal)
    const isBOM = product && isItemBOM(product)

    if (prop === "product") {
      if (!val) return "Product is required."
      else {
        if (!rootStore.isProductAvailable(val)) return `${val.toUpperCase()} is not available.`
        const found = newProducts.find(y => y.id !== id && y.product === val)
        if (found) return `${val.toUpperCase()} already exists`
      }
    } else if (prop === "country_of_origin" && deliveryCountry != "AU" && !val) {
      return "Country of origin is required."
    } else {
      if (prop === "qty" && (!val || parseFloat(val) == 0) && !isBOM) {
        return "Qty is required."
      } else if (prop === "price" && deliveryCountry != "AU" && (!val || parseFloat(val) == 0)) {
        return "Price is required."
      }

      if (parseFloat(val) < 0) return "Invalid number."
    }

    return ""
  }

  const onPropChanged = async (id, prop, val) => {
    let newProducts = [...products]
    const item = newProducts.find(x => x.id === id)
    if (!item) return

    if (prop === "product") {
      const other = newProducts.find(x => x.id !== id && x.product === val)
      if (other) other.validation[prop] = getErrorMessageOfProp(newProducts, other.id, prop, other.product)

      if (item[prop] != val) {
        const productVal = val.toUpperCase()
        const product = (GlobalData.ProductListSelection || []).find(x => x.id.toUpperCase() === productVal)
        if (product) {
          // remove child SKUs of current product if any
          newProducts = removeChildSKUsIfAny(newProducts, item)

          // if new product is BOM then explode
          if (isItemBOM(product)) {
            setLoading(true)
            // update the old value that will be used to update child SKUs
            item["oldProduct"] = productVal

            const components = await rootStore.BillOfMaterialStore.getBomDetails(productVal)
            addChildSKUs(newProducts, { ...item, product: productVal }, components)
            setLoading(false)
          }
        }
      }
    }

    if (item.line_id) item.action = "CHANGE"

    item[prop] = val

    if (prop === "erp_order" && newProducts.length > 0) {
      const groups = Object.groupBy(
        newProducts.filter(x => x.action !== "DELETE"),
        ({ erp_order }) => erp_order,
      )
      for (const [key, value] of Object.entries(groups)) {
        let idx = 1
        value.forEach(x => {
          if (x["line_num"] != idx) x["action"] = "CHANGE"
          x["line_num"] = idx++
        })
      }
    }

    // should not validate child SKUs of a BOM
    if (!item.parentProduct) item.validation[prop] = getErrorMessageOfProp(newProducts, id, prop, val)

    updateChildSKUsIfAny(newProducts, item)

    setProducts(newProducts)
  }

  const onDeleteClicked = id => {
    let newProducts = [...products]
    const item = newProducts.find(x => x.id === id)
    if (item) {
      newProducts = removeChildSKUsIfAny(newProducts, item)
      if (item.line_id) {
        item.action = "DELETE"
        setProducts(newProducts)
      } else {
        setProducts(newProducts.filter(x => x.id !== id))
      }
    }
  }

  const validateData = () => {
    let isValid = true
    const newProducts = [...products].filter(item => item.action !== "DELETE")
    newProducts.forEach(x => {
      if (!x.validation) {
        x.validation = {}
      }

      // should not validate child SKUs of a BOM
      if (x.parentProduct) return true

      x.validation.product = getErrorMessageOfProp(newProducts, x.id, "product", x.product)
      x.validation.qty = getErrorMessageOfProp(newProducts, x.id, "qty", x.qty)
      x.validation.price = getErrorMessageOfProp(newProducts, x.id, "price", x.price)
      x.validation.country_of_origin = getErrorMessageOfProp(newProducts, x.id, "country_of_origin", x.country_of_origin)

      if (x.validation.product || x.validation.qty || x.validation.price) isValid = false
    })

    setProducts(newProducts)

    return isValid
  }

  const removeChildSKUsIfAny = (products, parentItem) => {
    const result = []
    const parentSKU = parentItem.oldProduct || parentItem.product
    if (!parentSKU) return products

    products.forEach(x => {
      if (x.parentProduct !== parentSKU) {
        result.push(x)
        return true
      }

      if (x.line_id) {
        result.push({ ...x, action: "DELETE" })
      }
    })
    return result
  }

  const addChildSKUs = (products, parentItem, components) => {
    components.forEach((x, idx) => {
      const item = createProduct(parentItem, x, idx + 1)
      products.push(item)
    })
  }

  const updateChildSKUsIfAny = (products, parentItem) => {
    // a component the user removed stays removed
    const productsToBeUpdated = products.filter(x => x.parentProduct && x.parentProduct === parentItem.product && x.action !== "DELETE")
    productsToBeUpdated.forEach((x, idx) => {
      const item = products.find(p => p.product === x.product)
      if (item) {
        // components already saved in the order keep their own line number
        if (!item.line_id) item.line_num = generateChildLineNum(parentItem.line_num, idx + 1)
        item.erp_order = parentItem.erp_order
        item.qty = Number(parentItem.qty) * x.qty_per_item
        item.countryOfOrigin = parentItem.countryOfOrigin
        item.action = parentItem.action
      }
    })
  }

  const getLineNumberMaxValue = data => {
    const max = Math.max(...data.map(o => o.line_num), 0)
    return max + 1
  }

  const generateLineNum = () => {
    const realProducts = products.filter(item => item.erp_order === erp_order && item.action !== "DELETE" && !item.parentProduct)
    return getLineNumberMaxValue(realProducts)
  }

  const generateChildLineNum = (parentLineNum, childIdx) => {
    return Number(parentLineNum) + childIdx / 1000
  }

  const createProduct = (parentItem, childItem, childIdx) => {
    return {
      action: "NEW",
      product: childItem ? childItem.ITEM : "",
      erp_order: erp_order,
      qty: parentItem && childItem && parentItem.qty ? Number(parentItem.qty) * Number(childItem.QTY_NEEDED_PER_ITEM) : 0,
      countryOfOrigin: parentItem && childItem ? parentItem.countryOfOrigin : "",
      price: 0,
      line_id: "",
      line_num: parentItem ? generateChildLineNum(parentItem.line_num, childIdx) : generateLineNum(),
      parentProduct: parentItem ? parentItem.product : "",
      qty_per_item: childItem ? Number(childItem.QTY_NEEDED_PER_ITEM) : 0,
      validation: {},
      id: genId(),
    }
  }

  const handleAddProduct = () => {
    if (validateData()) {
      const newProducts = [...products]
      const product = createProduct()
      setProducts([...newProducts, product])
    }
  }

  return (
    <Card id="cardProducts">
      <CardHeader
        title="Product Information"
        action={
          <Button id="btnAddProduct" color="grey" variant="contained" onClick={handleAddProduct} disabled={isSubmitting} startIcon={<AddIcon />}>
            Add Product
          </Button>
        }
      />
      <CardContent>
        <CustomDataGrid
          getRowId={row => row.id}
          rows={products.filter(x => x.action !== "DELETE")}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "line_num", sort: CommonConstant.ASCENDING }],
            },
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
          }}
          pageSizeOptions={[10, 15, 20, 50]}
          rowHeight={85}
          disableRowSelectionOnClick
        />
        {loading && (
          <Backdrop sx={theme => ({ zIndex: theme.zIndex.drawer + 1 })} open={true}>
            <CircularProgress />
          </Backdrop>
        )}
      </CardContent>
    </Card>
  )
})

export default memo(SaleOrderProduct)
